import moment from 'moment';
import EventBus from 'eventing-bus';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState, useRef } from "react";
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ReplyIcon from '@material-ui/icons/Reply';
import LinkIcon from '@material-ui/icons/Link';

import CreateRelation from '../../../../components/Activities/prop/CreateRelation';
import { addComment, setEditComment, updateComment, setDeleteComment, deleteComment } from '../../../../store/Activities';
import './Comments.css';

const Comments = ({ singlePageData, activity, group, updateSinglePageReducer }) => {

    const dispatch = useDispatch();

    let [hovered, setHovered] = useState(false);
    // Replace boolean dropdowns with anchor element map for Material-UI Menu
    let [dropdowns, setDropdowns] = useState({}); // { [commentId]: anchorEl | null }
    let [isShowComment, setIsShowComment] = useState(false);
    let [form, setForm] = useState({ activityId: '', group, commentId: '', comment: '', loading: false, show: true });

    let initialDeleteForm = { activityId: '', commentId: '', objectType: singlePageData['objectType'], objectId: singlePageData['_id'] };
    let [deleteForm, setDeleteForm] = useState(initialDeleteForm);

    // Refs for ReactQuill focus management
    const editQuillRef = useRef(null);
    const addQuillRef = useRef(null);

    // Focus on edit ReactQuill when a comment is being edited
    useEffect(() => {
        if (form['commentId'] && editQuillRef.current) {
            const quill = editQuillRef.current.getEditor();
            quill.focus();
            // Set cursor to the end of the content
            const length = quill.getLength();
            quill.setSelection(length - 1, 0);
        }
    }, [form['commentId']]);

    // Focus on add ReactQuill when the add comment form is shown
    useEffect(() => {
        if (form['show'] && isShowComment) {
            // Small delay to ensure ReactQuill is fully rendered
            const focusTimer = setTimeout(() => {
                if (addQuillRef.current) {
                    const quill = addQuillRef.current.getEditor();
                    quill.focus();
                }
            }, 100);

            // Scroll to the add comment area
            const commentsArea = document.querySelector('.comment-show-box');
            if (commentsArea) commentsArea.scrollTo({ top: commentsArea['scrollHeight'], behavior: 'smooth' });

            return () => clearTimeout(focusTimer);
        }
    }, [form['show'], isShowComment]);

    const toggleDropdownAction = (id, e = null) => {
        // if e (event) provided -> open, otherwise toggle/close
        setDropdowns(prev => {
            const isOpen = prev[id];
            return {
                ...prev,
                [id]: e ? e.currentTarget : (isOpen ? null : null)
            };
        });
    };

    const openDropdown = (id, e) => {
        setDropdowns(prev => ({ ...prev, [id]: e.currentTarget }));
    };

    const closeDropdown = (id) => {
        setDropdowns(prev => ({ ...prev, [id]: null }));
    };

    // Helper action handlers for menu items (use existing logic where available)
    const onMenuEdit = (commentId) => {
        // open edit mode for this comment
        setForm(prev => ({ ...prev, commentId, comment: /* load existing comment text if available */ prev.comment }));
        // focus handled by existing effect watching form.commentId
        closeDropdown(commentId);
    };

    const onMenuDelete = (commentId) => {
        setDeleteForm(prev => ({ ...prev, commentId, activityId: activity?._id || '' }));
        // show confirm UI that your existing handleDelete uses (or call it directly)
        closeDropdown(commentId);
    };

    const onMenuReply = (commentId) => {
        setIsShowComment(true);
        setForm(prev => ({ ...prev, activityId: activity?._id || '', commentId: '', show: true }));
        // focus addQuillRef handled by existing effect
        closeDropdown(commentId);
    };

    const onMenuCopyLink = (commentId) => {
        const link = `${window.location.origin}${window.location.pathname}#comment-${commentId}`;
        navigator.clipboard?.writeText(link).catch(() => {});
        closeDropdown(commentId);
    };

    const addEditorModules = {
        toolbar: {
            container: '#add-comment-toolbar',
            handlers: {}
        },
        clipboard: { matchVisual: false }
    };

    const editEditorModules = {
        toolbar: {
            container: '#edit-comment-toolbar',
            handlers: {}
        },
        clipboard: { matchVisual: false }
    };

    const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'image'];

    const handleAddComment = () => {
        if (!form['comment']) return EventBus.publish('error', 'Please write a comment');
        setForm({ ...form, loading: true });

        dispatch(addComment({
            payload: { activityId: activity['_id'], commentId: '', content: form['comment'], objectType: singlePageData['objectType'], objectId: singlePageData['_id'] },
            successCallback: (newComment) => {

                console.log('******** newComment = ', newComment);
                
                // Update the comment with actual data from backend if needed
                const updatedComments = [...(activity['comments'] || []), newComment];
                updateSinglePageReducer('comments', updatedComments);
                setForm({ ...form, commentId: '', activityId: '', comment: '', loading: false, show: false });
            }
        }));
    };

    const handleEditComment = () => {
        setForm({ ...form, loading: true });

        // Update the comment in the comments array
        const updatedComments = (activity['comments'] || []).map(comment => comment['_id'] === form['commentId'] ? { ...comment, content: form['comment'] } : comment);
        updateSinglePageReducer('comments', updatedComments);

        dispatch(updateComment({
            payload: { activityId: form['activityId'], commentId: form['commentId'], content: form['comment'], objectType: singlePageData['objectType'], objectId: singlePageData['_id'] },
            successCallback: () => { setForm({ ...form, commentId: '', activityId: '', comment: '', loading: false, show: false }) }
        }));
    };

    const handleDelete = () => {

        // Remove the comment from the comments array
        const updatedComments = (activity['comments'] || []).filter(comment => comment['_id'] !== deleteForm['commentId']);
        updateSinglePageReducer('comments', updatedComments);
        dispatch(deleteComment({ ...deleteForm, successCallback: () => { setDeleteForm(initialDeleteForm) }}));

        setDeleteForm(initialDeleteForm);
    };

    return <>
        <div className="description-area">
            <div className="description-text">
                <button className="description-btn" onClick={() => { setIsShowComment(prev => !prev); setForm({ ...form, show: true }) }}>
                    <i className="icon">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.98573 10C4.64346 10 4.31831 9.82887 4.09583 9.52653L3.4113 8.61381C3.4113 8.61951 3.38277 8.60239 3.37137 8.60239H3.1603C1.20936 8.60239 0 8.07188 0 5.4421V3.1603C0 0.758704 1.46606 0.131209 2.69824 0.0228229C2.83515 0.00570938 2.99487 0 3.1603 0H6.81118C8.87621 0 9.97148 1.09527 9.97148 3.1603V5.4421C9.97148 5.60754 9.96577 5.76726 9.94295 5.92128C9.84027 7.13634 9.21278 8.60239 6.81118 8.60239H6.58301L5.87563 9.52653C5.65316 9.82887 5.328 10 4.98573 10ZM3.1603 0.855676C3.0291 0.855676 2.9036 0.86138 2.7838 0.872789C1.46036 0.986879 0.855676 1.71136 0.855676 3.1603V5.4421C0.855676 7.39875 1.46036 7.74671 3.1603 7.74671H3.38848C3.64518 7.74671 3.93611 7.88933 4.09583 8.0947L4.78038 9.01311C4.90588 9.18425 5.06561 9.18425 5.1911 9.01311L5.87563 8.10041C6.04107 7.87793 6.30348 7.74671 6.58301 7.74671H6.81118C8.26013 7.74671 8.9846 7.13634 9.09299 5.83572C9.1101 5.69881 9.11581 5.57331 9.11581 5.4421V3.1603C9.11581 1.56874 8.40274 0.855676 6.81118 0.855676H3.1603Z" fill="#4B5563" />
                            <path d="M4.98525 5.09989C4.66579 5.09989 4.41479 4.84318 4.41479 4.52944C4.41479 4.21569 4.6715 3.95898 4.98525 3.95898C5.29899 3.95898 5.5557 4.21569 5.5557 4.52944C5.5557 4.84318 5.3047 5.09989 4.98525 5.09989Z" fill="#4B5563" />
                            <path d="M6.81093 5.09989C6.49148 5.09989 6.24048 4.84318 6.24048 4.52944C6.24048 4.21569 6.49718 3.95898 6.81093 3.95898C7.12468 3.95898 7.38138 4.21569 7.38138 4.52944C7.38138 4.84318 7.12468 5.09989 6.81093 5.09989Z" fill="#4B5563" />
                            <path d="M3.16615 5.09989C2.8467 5.09989 2.5957 4.84318 2.5957 4.52944C2.5957 4.21569 2.85241 3.95898 3.16615 3.95898C3.4799 3.95898 3.7366 4.21569 3.7366 4.52944C3.7366 4.84318 3.4799 5.09989 3.16615 5.09989Z" fill="#4B5563" />
                        </svg>
                    </i>
                    {isShowComment ? 'Hide comments' : (activity?.['comments'] && activity['comments'].length > 0) ? activity['comments'].length + ' comments' : 'Add comment'}
                </button>
                <CreateRelation
                    activity={activity}
                    initialAssociations={activity?.['associations'] || {}}
                />
            </div>
            {isShowComment && (
                <div className="user-comment-area">
                    <div style={{ marginTop: '10px' }}>
                        {!form['show'] && <button className="description-btn" onClick={() => setForm({ ...form, show: true })}> Add comment</button>}
                        {form['show'] &&
                            <>
                                <div className="comment-editor">
                                    <div className="user-box">
                                        <div className="user-img-area">
                                            <div className="img-box"><img src={require('../../../../static/img/profile-photo.png')} alt='' /></div>
                                        </div>
                                    </div>
                                    <div className="editor-wrapper comment-editor-wrapper" onClick={(e) => e.stopPropagation()}>
                                        <ReactQuill
                                            ref={addQuillRef}
                                            formats={formats}
                                            value={form['comment']}
                                            modules={addEditorModules}
                                            bounds=".editor-wrapper"
                                            placeholder="Start typing to leave a comment..."
                                            theme="snow" // must be snow for custom toolbar
                                            onChange={(value) => setForm({ ...form, comment: value })}
                                            style={{ height: '50px' }}
                                        />

                                        {/* Custom Toolbar - Simplified to prevent IconPicker errors */}
                                        <div id="add-comment-toolbar" className="bottom-toolbar">
                                            <span className="ql-formats">
                                                <button className="ql-bold" />
                                                <button className="ql-italic" />
                                                <button className="ql-underline" />
                                                <button className="ql-strike" />
                                            </span>
                                            {/* <div className="ql-formats more-dropdown">
                                                <button className="more-toggle" onClick={() => toggleDropdownAction('showMore')}>More ▾</button>
                                                <span className="more-dropdown-content" onClick={(e) => e.stopPropagation()} style={{ display: dropdowns.showMore ? 'block' : 'none' }}>
                                                    <svg data-test-id="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28" style={{ position: 'absolute', pointerEvents: 'none', top: '100%', left: '7.7185px' }}><path clip-path="url(#:ra5:)" fill="none" stroke="#CBD6E2" stroke-width="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                                                    <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                                                        <select className="ql-font" />
                                                        <select className="ql-size" />
                                                        <select className="ql-color" />
                                                        <select className="ql-background" />
                                                        <button className="ql-blockquote" />
                                                        <button className="ql-list" value="ordered" />
                                                        <select className="ql-align" />
                                                    </span>
                                                </span>
                                            </div> */}
                                            
                                            <span className="ql-formats">
                                                <button className="ql-list" value="ordered" />
                                                <button className="ql-list" value="bullet" />
                                            </span>
                                            <span className="ql-formats">
                                                <button className="ql-link" />
                                                <button className="ql-image" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="btn-editor">
                                    <button onClick={() => handleAddComment()} className="btn-style-new" disabled={!form['comment'].trim() || form['loading']}>{form['loading'] ? 'Adding comment...' : 'Comment'}</button>
                                    <button className="btn-style-border" onClick={() => setForm({ ...form, show: false })}>Cancel</button>
                                </div>
                            </>
                        }
                    </div>
                    {/* Comments */}
                    {activity?.['comments']?.slice().reverse().map((comment, index) => {
                        return (
                            <div className="comment-show-box" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                                <div className="user-img-area">
                                    <div className="img-box"><img src={comment['createdBy']['profile'] || require('../../../../static/img/profile-photo.png')} alt='' /></div>
                                    <div className={`user-data-area title-area editing-title ${form['commentId'] === comment['_id'] ? 'is-editing' : ''}`}>
                                        <h4 className="comment-user-name">{comment?.['createdBy']?.['firstName'] + ' ' + comment?.['createdBy']?.['lastName'] || comment?.['createdBy']?.['email']}</h4>
                                        <p>{moment(comment?.['createdAt']).format('MMM D, YYYY hh:mm A')}</p>
                                        {form['commentId'] === comment['_id']
                                            ? <div className="edit-wrapper">
                                                <div className="editor-wrapper comment-editor-wrapper" onClick={(e) => e.stopPropagation()}>
                                                    <ReactQuill
                                                        ref={editQuillRef}
                                                        formats={formats}
                                                        theme="snow" // must be snow for custom toolbar
                                                        modules={editEditorModules}
                                                        bounds=".editor-wrapper"
                                                        placeholder="Start typing to leave a note..."
                                                        value={form['comment'] || comment?.['content']}
                                                        onChange={(value) => setForm({ ...form, comment: value })}
                                                        style={{ height: '50px' }}
                                                    />

                                                    {/* Custom Toolbar - Simplified to prevent IconPicker errors */}
                                                    <div id="edit-comment-toolbar" className="bottom-toolbar">
                                                        <span className="ql-formats">
                                                            <button className="ql-bold" />
                                                            <button className="ql-italic" />
                                                            <button className="ql-underline" />
                                                            <button className="ql-strike" />
                                                        </span>
                                                        {/* <div className="ql-formats more-dropdown">
                                                            <button className="more-toggle" onClick={() => toggleDropdownAction('showMore')} >More ▾</button>
                                                            <span
                                                                className="more-dropdown-content"
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{ display: dropdowns.showMore ? 'block' : 'none' }}

                                                            >
                                                                <svg data-test-id="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28" style={{ position: 'absolute', pointerEvents: 'none', top: '100%', left: '7.7185px' }}><path clip-path="url(#:ra5:)" fill="none" stroke="#CBD6E2" stroke-width="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                                                                <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                                                                    <select className="ql-font" />
                                                                    <select className="ql-size" />
                                                                    <select className="ql-color" />
                                                                    <select className="ql-background" />
                                                                    <button className="ql-blockquote" />
                                                                    <button className="ql-list" value="ordered" />
                                                                    <select className="ql-align" />
                                                                </span>
                                                            </span>
                                                        </div> */}

                                                        <span className="ql-formats">
                                                            <button className="ql-list" value="ordered" />
                                                            <button className="ql-list" value="bullet" />
                                                        </span>
                                                        <span className="ql-formats">
                                                            <button className="ql-link" />
                                                            <button className="ql-image" />
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="edit-actions">
                                                    <button className="btn-style-new" onClick={() => handleEditComment()}>Save</button>
                                                    <button className="btn-style-border" onClick={() => setForm({ commentId: '', activityId: '', comment: '' })}>Cancel</button>
                                                </div>
                                            </div>
                                            : <span className="title-text" onClick={() => setForm({ commentId: comment['_id'], activityId: activity['_id'], comment: comment?.['content'] })} style={{ position: 'relative' }}>
                                                <div dangerouslySetInnerHTML={{ __html: comment?.['content'] }} />
                                                {hovered && <i className="edit-icon"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="Edit" class="StyledIcon-sc-1jfhstb-0 iTgcdA"><path d="m11.86 27.84.25-.1h.1c.08-.05.16-.11.23-.17l12.15-12.08c.28-.28.44-.65.43-1.05-.02-.84-.71-1.5-1.55-1.48-.38 0-.75.16-1.03.43L11.43 24.3l-.88-.87 10.97-10.87c.5-.66.38-1.6-.28-2.11a1.5 1.5 0 0 0-1.82 0L8.41 21.32l-.89-.87L18.54 9.56c.53-.64.45-1.59-.19-2.12a1.51 1.51 0 0 0-1.94.01L4.32 19.38c-.12.13-.21.27-.28.43v.14s.01.08 0 .13l-2.02 7.98q.03.18 0 .36c0 .82.68 1.49 1.5 1.48.12.02.25.02.38 0l8.07-1.98-.1-.09ZM25.93 3.06a3.527 3.527 0 0 0-4.9 0L19.42 4.6c-.28.28-.44.66-.44 1.06s.16.77.44 1.05l5.87 5.8c.59.59 1.55.59 2.14 0l1.56-1.54a3.416 3.416 0 0 0 0-4.84l-3.07-3.07Z"></path></svg></i>}
                                            </span>}
                                    </div>
                                </div>
                                <div className="right-box">
                                    {/* <button>
                                        <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4.22117 14.8727C3.75641 14.8727 3.32212 14.7127 3.00974 14.4155C2.61355 14.0422 2.42307 13.4784 2.49164 12.8688L2.77355 10.4003C2.82688 9.93551 3.10879 9.31837 3.43641 8.98313L9.69164 2.36217C11.2535 0.708841 12.884 0.663127 14.5374 2.22503C16.1907 3.78694 16.2364 5.41741 14.6745 7.07075L8.41926 13.6917C8.09926 14.0346 7.50498 14.3546 7.04022 14.4307L4.58688 14.8498C4.45736 14.8574 4.34307 14.8727 4.22117 14.8727ZM12.1374 2.21741C11.5507 2.21741 11.0402 2.58313 10.5221 3.1317L4.26688 9.76027C4.1145 9.92027 3.93926 10.3012 3.90879 10.5222L3.62688 12.9907C3.59641 13.2422 3.65736 13.4479 3.7945 13.5774C3.93164 13.7069 4.13736 13.7527 4.38879 13.7146L6.84212 13.2955C7.06307 13.2574 7.42879 13.0593 7.58117 12.8993L13.8364 6.27837C14.7812 5.27265 15.124 4.34313 13.745 3.04789C13.1355 2.46122 12.6097 2.21741 12.1374 2.21741Z" fill="#2E2C9A" />
                                            <path d="M13.2116 8.34256C13.1964 8.34256 13.1735 8.34256 13.1583 8.34256C10.7811 8.10637 8.86877 6.30065 8.50305 3.93875C8.45734 3.62637 8.67067 3.33684 8.98305 3.28351C9.29543 3.23779 9.58496 3.45113 9.63829 3.76351C9.92781 5.60732 11.4211 7.02446 13.2802 7.20732C13.5926 7.23779 13.8211 7.5197 13.7907 7.83208C13.7526 8.1216 13.5011 8.34256 13.2116 8.34256Z" fill="#2E2C9A" />
                                        </svg>
                                    </button> */}
                                    <button onClick={() => { setDeleteForm({ ...deleteForm, commentId: comment['_id'], activityId: activity['_id'] }) }}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14 4.48681C13.9866 4.48681 13.9666 4.48681 13.9466 4.48681C10.42 4.13348 6.89997 4.00015 3.41331 4.35348L2.05331 4.48681C1.77331 4.51348 1.52664 4.31348 1.49997 4.03348C1.47331 3.75348 1.67331 3.51348 1.94664 3.48681L3.30664 3.35348C6.85331 2.99348 10.4466 3.13348 14.0466 3.48681C14.32 3.51348 14.52 3.76015 14.4933 4.03348C14.4733 4.29348 14.2533 4.48681 14 4.48681Z" fill="#2E2C9A" />
                                            <path d="M5.66666 3.8135C5.64 3.8135 5.61333 3.8135 5.58 3.80683C5.31333 3.76016 5.12666 3.50016 5.17333 3.2335L5.32 2.36016C5.42666 1.72016 5.57333 0.833496 7.12666 0.833496H8.87333C10.4333 0.833496 10.58 1.7535 10.68 2.36683L10.8267 3.2335C10.8733 3.50683 10.6867 3.76683 10.42 3.80683C10.1467 3.8535 9.88666 3.66683 9.84666 3.40016L9.7 2.5335C9.60666 1.9535 9.58666 1.84016 8.88 1.84016H7.13333C6.42666 1.84016 6.41333 1.9335 6.31333 2.52683L6.16 3.3935C6.12 3.64016 5.90666 3.8135 5.66666 3.8135Z" fill="#2E2C9A" />
                                            <path d="M10.14 15.1667H5.86C3.53334 15.1667 3.44 13.88 3.36667 12.84L2.93334 6.12672C2.91334 5.85338 3.12667 5.61338 3.4 5.59338C3.68 5.58005 3.91334 5.78672 3.93334 6.06005L4.36667 12.7734C4.44 13.7867 4.46667 14.1667 5.86 14.1667H10.14C11.54 14.1667 11.5667 13.7867 11.6333 12.7734L12.0667 6.06005C12.0867 5.78672 12.3267 5.58005 12.6 5.59338C12.8733 5.61338 13.0867 5.84672 13.0667 6.12672L12.6333 12.84C12.56 13.88 12.4667 15.1667 10.14 15.1667Z" fill="#2E2C9A" />
                                            <path d="M9.10669 11.5H6.88669C6.61335 11.5 6.38669 11.2733 6.38669 11C6.38669 10.7267 6.61335 10.5 6.88669 10.5H9.10669C9.38002 10.5 9.60669 10.7267 9.60669 11C9.60669 11.2733 9.38002 11.5 9.10669 11.5Z" fill="#2E2C9A" />
                                            <path d="M9.66668 8.8335H6.33334C6.06001 8.8335 5.83334 8.60683 5.83334 8.3335C5.83334 8.06016 6.06001 7.8335 6.33334 7.8335H9.66668C9.94001 7.8335 10.1667 8.06016 10.1667 8.3335C10.1667 8.60683 9.94001 8.8335 9.66668 8.8335Z" fill="#2E2C9A" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>

        {/* Delete Comment Modal */}
        <Modal isOpen={deleteForm['commentId']} className={`main-modal new-user-modal modal-dialog-centered create-label-modal`}>
            <ModalHeader toggle={() => setDeleteForm(initialDeleteForm)}></ModalHeader>
            <ModalBody>
                <div className='content-area'>
                    <div className='delete-modal'>
                        <h4>
                            <i className='icon'>
                                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                    <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fill-opacity='0.15' />
                                    <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                                    <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                                    <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                                </svg>
                            </i>
                            Delete Comment
                        </h4>
                        <p>Are your sure you want to delete the comment?</p>
                        <div className='form-area'>
                            <div className='btns-box text-right'>
                                <button className='btn-style-border' onClick={() => setDeleteForm(initialDeleteForm)}>Cancel</button>
                                <button className='btn-style-new' onClick={() => handleDelete()} >Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal >
    </>
};

export default Comments;